/**
 * Local password-strength estimation (no external deps — supply-chain safe).
 * Inspired by zxcvbn heuristics: entropy, common passwords, patterns, repetition.
 */

const MIN_LENGTH = 12;
/** Minimum score (0–4) required for Tresor setup / password reset. */
export const MIN_PASSWORD_SCORE = 3;

const SCORE_LABELS = ['Sehr schwach', 'Schwach', 'Mäßig', 'Gut', 'Sehr stark'];

/** Top passwords & trivial variants (DE + EN). Kept compact — high hit rate. */
const COMMON_PASSWORDS = new Set([
  'password', 'passwort', '123456', '12345678', '123456789', '1234567890',
  'qwerty', 'qwertz', 'asdfgh', 'asdfghjkl', 'zxcvbn', 'zxcvbnm',
  'letmein', 'welcome', 'monkey', 'dragon', 'master', 'login', 'admin',
  'iloveyou', 'sunshine', 'princess', 'football', 'shadow', 'superman',
  'trustno1', 'batman', 'access', 'hello', 'charlie', 'donald', 'michael',
  'jennifer', 'thomas', 'andreas', 'martin', 'stefan', 'daniel', 'christian',
  'sommer', 'winter', 'fruehling', 'herbst', 'sonne', 'mond', 'sterne',
  'deutschland', 'berlin', 'muenchen', 'hamburg', 'koeln', 'frankfurt',
  'datenputzer', 'tresor', 'geheim', 'geheimnis', 'hallo', 'test', 'test123',
  'password1', 'password123', 'passwort1', 'passwort123', 'qwerty123',
  'abc123', 'abcd1234', '111111', '000000', '123123', '121212', '654321',
  'killer', 'mustang', 'freedom', 'whatever', 'qazwsx', 'baseball', 'soccer',
  'liverpool', 'chelsea', 'bayern', 'borussia', 'schalke', 'fussball',
  'mercedes', 'porsche', 'volkswagen', 'bmw', 'audi', 'computer', 'internet',
  'schatz', 'liebling', 'maus', 'hase', 'kaffee', 'bier', 'fussball123',
  'sommer2024', 'sommer2025', 'sommer2026', 'winter2024', 'winter2025', 'winter2026',
  'passwort12', 'passwort1234', 'password12', 'password1234',
]);

const KEYBOARD_ROWS = [
  'qwertyuiop', 'qwertzuiop', 'asdfghjkl', 'yxcvbnm', '1234567890',
  'abcdefghijklmnopqrstuvwxyz',
];

const SEQUENCES = [
  '0123456789', '9876543210', 'abcdefghijklmnopqrstuvwxyz', 'zyxwvutsrqponmlkjihgfedcba',
];

function stripToAlphanumeric(pw) {
  return pw
    .toLowerCase()
    .replace(/[äàáâã]/g, 'a')
    .replace(/[öòóôõ]/g, 'o')
    .replace(/[üùúû]/g, 'u')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

function normalizeForDict(pw) {
  return stripToAlphanumeric(pw)
    .replace(/[o]/g, '0')
    .replace(/[il]/g, '1')
    .replace(/[3e]/g, '3')
    .replace(/[4a]/g, '4')
    .replace(/[5s]/g, '5')
    .replace(/[7t]/g, '7');
}

const WEAK_WORD_FRAGMENTS = [
  'passwort', 'password', 'sommer', 'winter', 'fruehling', 'herbst', 'geheim',
  'tresor', 'datenputzer', 'welcome', 'admin', 'login', 'master', 'secret',
  'hallo', 'hello', 'test', 'fussball', 'bayern', 'berlin', 'deutschland',
];

function matchesCommonPassword(pw) {
  const lower = pw.toLowerCase();
  const bare = stripToAlphanumeric(pw);
  const leet = normalizeForDict(pw);
  if (COMMON_PASSWORDS.has(lower) || COMMON_PASSWORDS.has(bare) || COMMON_PASSWORDS.has(leet)) {
    return true;
  }
  for (const entry of COMMON_PASSWORDS) {
    if (bare.includes(entry) || entry.includes(bare)) {
      if (bare.length <= entry.length + 4) return true;
    }
  }
  for (const word of WEAK_WORD_FRAGMENTS) {
    if (bare.includes(word) && bare.length < word.length + 10) return true;
  }
  if (/^[a-z]+(19|20)\d{2}[0-9!#.$%&*]?$/i.test(pw.replace(/[^a-z0-9]/gi, ''))) {
    return true;
  }
  return false;
}

function hasKeyboardRun(s, minLen = 4) {
  const lower = s.toLowerCase();
  for (const row of KEYBOARD_ROWS) {
    for (let len = Math.min(lower.length, 12); len >= minLen; len--) {
      for (let i = 0; i <= lower.length - len; i++) {
        const sub = lower.slice(i, i + len);
        if (row.includes(sub) || row.includes([...sub].reverse().join(''))) return true;
      }
    }
  }
  return false;
}

function hasSequence(s, minLen = 4) {
  const lower = s.toLowerCase();
  for (const seq of SEQUENCES) {
    for (let len = Math.min(lower.length, 12); len >= minLen; len--) {
      for (let i = 0; i <= lower.length - len; i++) {
        const sub = lower.slice(i, i + len);
        if (seq.includes(sub) || seq.includes([...sub].reverse().join(''))) return true;
      }
    }
  }
  return false;
}

function repeatPenalty(s) {
  if (/(.)\1{2,}/.test(s)) return 12;
  const chunks = s.match(/.{1,3}/g) || [];
  for (let size = 1; size <= 3; size++) {
    if (s.length >= size * 3) {
      const pattern = s.slice(0, size);
      let reps = 0;
      for (let i = 0; i + size <= s.length; i += size) {
        if (s.slice(i, i + size) === pattern) reps++;
      }
      if (reps >= 3) return 10;
    }
  }
  return 0;
}

function charsetSize(pw) {
  let size = 0;
  if (/[a-z]/.test(pw)) size += 26;
  if (/[A-Z]/.test(pw)) size += 26;
  if (/[0-9]/.test(pw)) size += 10;
  if (/[^a-zA-Z0-9]/.test(pw)) size += 32;
  return size || 1;
}

function entropyBits(pw) {
  const size = charsetSize(pw);
  return pw.length * Math.log2(size);
}

function mapEntropyToScore(bits, penalties) {
  const effective = Math.max(0, bits - penalties);
  if (effective < 28) return 0;
  if (effective < 36) return 1;
  if (effective < 48) return 2;
  if (effective < 60) return 3;
  return 4;
}

/**
 * @param {string} password
 * @returns {{
 *   score: number,
 *   label: string,
 *   feedback: string[],
 *   barPercent: number,
 *   acceptable: boolean,
 *   entropyBits: number,
 * }}
 */
export function evaluatePasswordStrength(password) {
  const feedback = [];
  const pw = password || '';

  if (!pw) {
    return {
      score: 0,
      label: SCORE_LABELS[0],
      feedback: ['Mindestens 12 Zeichen mit Groß-/Kleinbuchstaben, Ziffern und Sonderzeichen.'],
      barPercent: 0,
      acceptable: false,
      entropyBits: 0,
    };
  }

  if (pw.length < MIN_LENGTH) {
    feedback.push(`Mindestens ${MIN_LENGTH} Zeichen erforderlich (aktuell: ${pw.length}).`);
  }

  let penalties = 0;
  const normalized = normalizeForDict(pw);

  if (matchesCommonPassword(pw)) {
    penalties += 40;
    feedback.push('Das ist ein sehr häufiges Passwort oder eine Abwandlung davon. Nehmen Sie etwas Eigenes.');
  } else if (/(19|20)\d{2}/.test(normalized) || /(19|20)\d{2}/.test(pw)) {
    penalties += 12;
    feedback.push('Jahreszahlen in Kombination mit Wörtern sind leicht erratbar.');
  }

  if (hasKeyboardRun(pw)) {
    penalties += 14;
    feedback.push('Tastaturmuster (z. B. „qwertz“ oder „asdf“) vermeiden.');
  }

  if (hasSequence(pw)) {
    penalties += 12;
    feedback.push('Aufeinanderfolgende Zeichen (z. B. „1234“ oder „abcd“) vermeiden.');
  }

  penalties += repeatPenalty(pw);

  const classes = [
    /[a-z]/.test(pw),
    /[A-Z]/.test(pw),
    /[0-9]/.test(pw),
    /[^a-zA-Z0-9]/.test(pw),
  ].filter(Boolean).length;

  if (classes < 3) {
    penalties += 10;
    feedback.push('Kombinieren Sie Groß-/Kleinbuchstaben, Ziffern und Sonderzeichen.');
  }

  if (/^[a-zA-Z]+$/.test(pw)) {
    penalties += 6;
    feedback.push('Nur Buchstaben. Fügen Sie Ziffern oder Sonderzeichen hinzu.');
  }

  if (/^\d+$/.test(pw)) {
    penalties += 20;
    feedback.push('Nur Ziffern sind für einen Tresor zu schwach.');
  }

  const bits = entropyBits(pw);
  let score = mapEntropyToScore(bits, penalties);

  if (pw.length < MIN_LENGTH) score = Math.min(score, 1);
  if (matchesCommonPassword(pw)) score = Math.min(score, 1);

  const acceptable = pw.length >= MIN_LENGTH && score >= MIN_PASSWORD_SCORE;

  if (acceptable && feedback.length === 0) {
    feedback.push('Gutes Passwort für den Tresor.');
  } else if (!acceptable && score >= MIN_PASSWORD_SCORE && pw.length < MIN_LENGTH) {
  } else if (!acceptable && feedback.length === 0) {
    feedback.push('Passwort ist noch zu leicht erratbar. Wählen Sie es länger und zufälliger.');
  }

  return {
    score,
    label: SCORE_LABELS[score],
    feedback: [...new Set(feedback)].slice(0, 3),
    barPercent: Math.min(100, Math.round(((score + 1) / 5) * 100)),
    acceptable,
    entropyBits: Math.round(bits),
  };
}

/** User-facing error when password fails policy checks. */
export function getPasswordStrengthError(password) {
  const result = evaluatePasswordStrength(password);
  if (result.acceptable) return null;
  if ((password || '').length < MIN_LENGTH) {
    return `Das Passwort muss mindestens ${MIN_LENGTH} Zeichen lang sein.`;
  }
  const hint = result.feedback[0] || 'Bitte ein stärkeres Passwort wählen.';
  return `Passwort zu schwach (${result.label}). ${hint}`;
}
